import _ from "lodash";
import { Middleware } from "redux";

import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Events, ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { updateIntegratedDocument } from "../../app/integratedDocuments/actions";
import { updateOverviewDocument } from "../../component/auxiliaryOverviewEngine/actions";
import { assertNonNullable, assertTrue } from "../../utilities/assert";
import { getDocument } from "../../utilities/rpcHelper";
import { isDate } from "../../utilities/typeGuardUtils";

import { ANTRAG_DETAIL_ACTIVITY_ID } from "../processOverview/store/actions";
import { requestUpdateDueWiedervorlagenCount } from "../wiedervorlagen/actions";

import { FRISTEN_ACTIVITY, SortingPaths } from "./constants";
import { dataModels } from "./moduleResources";

import SingleDocumentData = FormActivity.Data.SingleDocumentData;

/**
 * Schließt die Fristen-Activity, wenn der Prozess gestartet wird (Event-Button).
 * Andernfalls wird ein Bug ausgelöst, der durch die Interaktion zwischen Workflows und Client entsteht und eine Endlosschleife
 * zur Folge hat.
 */
export const closeFristenMiddleware: Middleware = api => next => action => {
	if (FormEngineActions.event.match(action) && Events.eventButton.match(action.payload.engineEvent)) {
		const { engineEvent } = action.payload;
		if (Events.eventButton.match(engineEvent)) {
			if (engineEvent.payload.name === "proceed" && ActivitySelectors.activityById(FRISTEN_ACTIVITY)) {
				api.dispatch(ActivityActions.cancel({ activityId: FRISTEN_ACTIVITY }));
			}
		}
	}
	return next(action);
};

/**
 * Modifiziert ein in der AuxiliaryOverview bearbeitetes Fristendokument (Checkbox "Erfüllt") dahingehend,
 * dass der Flag "Erledigt" auch für die integrierte WVL gesetzt wird.
 */
export const modifyUpdatedOverviewDocument: Middleware = api => next => action => {
	if (updateOverviewDocument.match(action) && action.payload.originalDocument.modelId === dataModels.FRIST_D) {
		const fristErfuellt = action.payload.updates[0][1];
		if (fristErfuellt !== undefined) {
			const wvlErledigtPath = ModelPath.fromString(SortingPaths.WVLErledigt.replace(/\./g, "/")).map(it => ({
				...it,
				index: 1
			}));
			action.payload.updates.push([wvlErledigtPath, fristErfuellt]);
		}
	}
	return next(action);
};

/**
 * Horcht auf das Commit_Done-Event des Fristen-Modals, wenn dieses bearbeitet und gespeichert wird.
 * Danach muss die Zahl der Wiedervorlagen (Badge am Knopf "Wiedervorlagen") aktualisiert werden
 */
export const commitFristMiddleware: Middleware = api => next => action => {
	if (ActivityActions.commit.done.match(action)) {
		const store = api.getState();
		const committedActivity = ActivitySelectors.activityById(action.payload.params.activityId)(store);
		if (committedActivity?.descriptor?.model === dataModels.FRIST_D && committedActivity.descriptor.processId) {
			const documentReference = (action.payload.result as ActivityActions.SaveResult).instance;
			assertNonNullable(documentReference);

			// Optimistic UI updates for responsiveness: Start by immediately integrating the committed changes into
			// a potential outer document

			const committedData = ActivitySelectors.data(committedActivity.id)(store);
			assertTrue(FormActivity.Data.SingleDocumentData.isInstance(committedData));

			const modelId = committedData.document.modelId;
			const subdocumentId = committedData.document.id.split("/")[1];
			const processId = committedActivity.descriptor.processId;

			api.dispatch(
				updateIntegratedDocument({
					subdocumentId,
					processId,
					subdocument: committedData.document,
					mode: "override"
				})
			);

			// We now need to reload from server to catch any serverside modifications (esp. the IDs of subdocuments)
			const documentService = new DocumentServiceFactory().getDocumentService();
			const engineState = FormEngineSelectors.engineState(committedActivity.id)(api.getState());
			assertNonNullable(engineState);
			const documentModel = ModelSelectors.documentModel()(engineState);
			getDocument(documentReference).then(persistedData => {
				assertNonNullable(persistedData?.document);
				const parsedDocument = documentService.parseDates(persistedData.document, documentModel);

				api.dispatch(
					updateIntegratedDocument({
						subdocumentId,
						processId,
						subdocument: { modelId, ...parsedDocument },
						mode: "merge"
					})
				);

				api.dispatch(requestUpdateDueWiedervorlagenCount({ activityId: ANTRAG_DETAIL_ACTIVITY_ID }));
			});
		}
	}
	return next(action);
};

/** Belegt das Feld "Beschreibung" der integrierten Wiedervorlage, sobald ein Fristende ausgewählt wird **/
export const prefillWiedervorlageBeschreibungFristMiddleware: Middleware = api => next => action => {
	if (
		FormEngineActions.event.match(action) &&
		Events.valueChange.match(action.payload.engineEvent) &&
		action.payload.engineEvent.payload.path
			.slice(-2)
			.map(it => it.elementName)
			.join("/") === "Frist/Fristende" &&
		!!action.payload.engineEvent.payload.value
	) {
		const activity = ActivitySelectors.activityById(action.payload.activityId)(api.getState());
		assertNonNullable(activity);

		const value = action.payload.engineEvent.payload.value;
		assertTrue(isDate(value));

		const data = ActivitySelectors.data(activity.id)(api.getState()) as SingleDocumentData;

		const fristenPath = action.payload.engineEvent.payload.path
			.slice(0, -1)
			.map(it => it.elementName)
			.join(".");
		const wvlBeschreibungPath = `${fristenPath}.IntegrierteWiedervorlage.Wiedervorlage.Beschreibung`;
		const fristBezeichnungPath = `${fristenPath}.Bezeichnung`;

		if (!_.get(data.document, wvlBeschreibungPath)) {
			api.dispatch(
				ActivityActions.setData({
					activityId: activity.id,
					data: {
						...data,
						document: _.set(
							data.document,
							wvlBeschreibungPath,
							`Fristende ${_.get(data.document, fristBezeichnungPath)}: ${value.toLocaleDateString(
								"de-DE",
								{
									year: "numeric",
									month: "2-digit",
									day: "2-digit"
								}
							)}`
						)
					}
				})
			);
		}
	}
	return next(action);
};
